'use client';

import type { ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { HStack } from '@terra/ui-styled-system';
import { Add01Icon, RefreshIcon, UnavailableIcon } from '@terra/ui-icons';
import { automationId } from '@terra/shared-utils';
import { Badge } from '../../../components/badge';
import { IconButton } from '../../../components/icon-button';
import { Skeleton } from '../../../components/skeleton';
import { Spinner } from '../../../components/spinner';
import { Tooltip } from '../../../components/tooltip';
import { HeadingSection } from '../../../components/typography';
import { ConfirmationDialog } from '../../confirmation-dialog/ConfirmationDialog';
import { SettingsEmptyState } from '../../settings/SettingsEmptyState';
import { SettingsCard } from '../../settings/SettingsCard';
import { SettingsRow } from '../../settings/SettingsRow';
import { SettingsSection } from '../../settings/SettingsSection';
import { InternalNetworkEnrollmentSecretsDialog } from './InternalNetworkEnrollmentSecretsDialog';
import {
  resolveSensorEnrollmentStatusColor,
  type InternalNetworkSensorEnrollmentRow,
} from './internal-connection.model';
import {
  useInternalNetworkSensorEnrollmentActions,
  type InternalNetworkSensorEnrollmentCallbacks,
} from './useInternalNetworkSensorEnrollmentActions';

export type InternalNetworkSensorEnrollmentsPanelProps =
  InternalNetworkSensorEnrollmentCallbacks & {
    enrollments: InternalNetworkSensorEnrollmentRow[];
    isLoading?: boolean;
    isReadOnly?: boolean;
    secretsDialogConnectionProgress?: ReactNode;
  };

type SensorEnrollmentRowActionsProps = {
  isRotating: boolean;
  isRevoking: boolean;
  onRotate: () => void;
  onRevoke: () => void;
};

function SensorEnrollmentRowActions({
  isRotating,
  isRevoking,
  onRotate,
  onRevoke,
}: SensorEnrollmentRowActionsProps) {
  const t = useTranslations(
    'designSystem.networkSettings.internalConnection.enrollments',
  );

  return (
    <>
      <Tooltip title={t('regenerate')}>
        <IconButton
          size="sm"
          variant="ghost"
          aria-label={t('regenerate')}
          onClick={onRotate}
          disabled={isRotating}
        >
          {isRotating ? <Spinner size="sm" /> : <RefreshIcon size={16} />}
        </IconButton>
      </Tooltip>
      <Tooltip title={t('revoke')}>
        <IconButton
          size="sm"
          variant="ghost"
          aria-label={t('revoke')}
          onClick={onRevoke}
          disabled={isRevoking}
          {...automationId('internal-network-enrollment-revoke')}
        >
          {isRevoking ? <Spinner size="sm" /> : <UnavailableIcon size={16} />}
        </IconButton>
      </Tooltip>
    </>
  );
}

export function InternalNetworkSensorEnrollmentsPanel({
  enrollments,
  isLoading,
  isReadOnly,
  secretsDialogConnectionProgress,
  ...callbacks
}: InternalNetworkSensorEnrollmentsPanelProps) {
  const t = useTranslations(
    'designSystem.networkSettings.internalConnection.enrollments',
  );
  const tCommon = useTranslations('designSystem.common');
  const actions = useInternalNetworkSensorEnrollmentActions(callbacks);

  return (
    <>
      <SettingsSection
        title={
          <HeadingSection
            {...automationId('internal-network-sensor-enrollments-title')}
          >
            {t('title')}
          </HeadingSection>
        }
      >
        <SettingsCard>
          <SettingsRow
            label={t('title')}
            value={t('description')}
            control={
              isReadOnly ? null : (
                <Tooltip title={t('create')}>
                  <IconButton
                    size="xs"
                    variant="outline"
                    aria-label={t('create')}
                    onClick={() => void actions.createEnrollment()}
                    disabled={actions.isCreating}
                    {...automationId('internal-network-create-enrollment')}
                  >
                    {actions.isCreating ? (
                      <Spinner size="sm" />
                    ) : (
                      <Add01Icon size={16} />
                    )}
                  </IconButton>
                </Tooltip>
              )
            }
          />
          {isLoading ? (
            <Skeleton h="80px" w="full" />
          ) : enrollments.length === 0 ? (
            <SettingsEmptyState>{t('empty')}</SettingsEmptyState>
          ) : (
            enrollments.map((enrollment) => (
              <SettingsRow
                key={enrollment.id}
                label={enrollment.id}
                value={
                  enrollment.lastHeartbeatAt
                    ? `${t('columns.lastHeartbeat')}: ${new Date(enrollment.lastHeartbeatAt).toLocaleString()}`
                    : t('columns.lastHeartbeat')
                }
                control={
                  <HStack gap={2}>
                    <Badge
                      variant="outline"
                      size="sm"
                      colorPalette={resolveSensorEnrollmentStatusColor(
                        enrollment.deploymentStatus,
                      )}
                      {...automationId(
                        `internal-network-enrollment-status-${enrollment.deploymentStatus.toLowerCase()}`,
                      )}
                    >
                      {t(`status.${enrollment.deploymentStatus}`)}
                    </Badge>
                    {enrollment.deploymentStatus !== 'REVOKED' &&
                    !isReadOnly ? (
                      <SensorEnrollmentRowActions
                        isRotating={
                          actions.rotatingEnrollmentId === enrollment.id
                        }
                        isRevoking={
                          actions.revokingEnrollmentId === enrollment.id
                        }
                        onRotate={() =>
                          actions.requestRotateEnrollmentKey(enrollment.id)
                        }
                        onRevoke={() =>
                          void actions.revokeEnrollment(enrollment.id)
                        }
                      />
                    ) : null}
                  </HStack>
                }
              />
            ))
          )}
        </SettingsCard>
      </SettingsSection>

      <InternalNetworkEnrollmentSecretsDialog
        dialog={actions.secretsDialog}
        onClose={actions.closeSecretsDialog}
        connectionProgress={secretsDialogConnectionProgress}
      />

      <ConfirmationDialog
        isOpen={actions.pendingRotateEnrollmentId !== null}
        loading={actions.rotatingEnrollmentId !== null}
        variant="warning"
        title={t('regenerateConfirmTitle')}
        description={t('regenerateConfirmDescription')}
        confirmText={t('regenerateConfirmAction')}
        cancelText={tCommon('cancel')}
        onOpenChange={({ open }) => {
          if (!open) {
            actions.cancelRotateEnrollmentKey();
          }
        }}
        onConfirm={() => void actions.confirmRotateEnrollmentKey()}
        onCancel={actions.cancelRotateEnrollmentKey}
      />
    </>
  );
}
